import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LinearProgress } from '@mafc/trolly';
import { useBreakpoints } from '@mafc/hooks';
import useAction from 'hooks/useAction';
import usePayment from './hooks/usePayment';
import useStepsProgress from 'pages/Checkout/hooks/useStepsProgress.hook';
import {
  fetchCreditCards,
  fetchPaymentModes,
  setPaymentMode,
} from 'pages/Checkout/redux/actions/payment.action';
import EPaymentMethods from './PamentMethods.enum';

import Card from '../Card';
import FEATURES_CONFIG from 'config/features';
import { IAppState, useAnalytic } from '@mafc/common';
import PaymentMethodList from './components/PaymentMethodList';
import SecurePaymentsLogo from './components/SecurePaymentsLogo';
import * as S from './styles/Payment.style';
import { useFeature } from '@mafc/appshell';
import { ChangeCurrency } from './components/ChangeCurrency';
import useLbpToUsd from 'hooks/useLbpToUsd';

const { enableHostedCreditCard } = FEATURES_CONFIG.settings;

const Payment: React.FC = () => {
  const [setPaymentModeAction] = useAction(setPaymentMode);
  const device = useBreakpoints();

  const store = useSelector((state: IAppState) => state);

  const {
    totalPrice: { value: cartAmount },
    paymentMode: cartPaymentMode, // this will be used if the user refreshed the page
  } = store.cart.cartData;

  const { BrazeSetCustomUserAttribute } = useAnalytic();

  const {
    payment: {
      paymentMode: { type },
      paymentDetails,
      loading,
      creditCards: { list },
    },
  } = usePayment();

  const [fetchPaymentModesAction] = useAction(fetchPaymentModes);

  useEffect(() => {
    fetchPaymentModesAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaymentModesAction]);

  const [
    { deliverTo, payment: paymentStatus },
    { checkIfEnabled, setStepPaymentStatus },
  ] = useStepsProgress();

  const [fetchCreditCardsAction] = useAction(fetchCreditCards);
  const isEnabled = deliverTo && checkIfEnabled(deliverTo, paymentStatus);
  const isSavedCardEnabled = useFeature('checkout.saved_cards.enabled').on;
  // Set initial payment mode if there is any
  useEffect(() => {
    if (
      cartPaymentMode === EPaymentMethods.COD ||
      cartPaymentMode === EPaymentMethods.CASHBACK ||
      cartPaymentMode === EPaymentMethods.DEBIT_CARD
    ) {
      setPaymentModeAction(cartPaymentMode, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture saved card for braze
  useEffect(() => {
    localStorage.setItem('saved_card', list.length > 0 ? '1' : '0');
  }, [list, BrazeSetCustomUserAttribute]);

  // Close the selection when the user select payment method
  // update step progress
  useEffect(() => {
    if (!type) {
      setStepPaymentStatus(false);
      return;
    }

    // Saved credit card, COD and Cashback
    if (
      paymentDetails.id ||
      type === EPaymentMethods.COD ||
      type === EPaymentMethods.CARD_ON_DELIVERY ||
      type === EPaymentMethods.CASHBACK ||
      type === EPaymentMethods.DEBIT_CARD ||
      (type === EPaymentMethods.CREDIT_CARD && enableHostedCreditCard)
    ) {
      setStepPaymentStatus(true);
    }

    // New credit card
    if (
      (type === EPaymentMethods.CREDIT_CARD && !paymentDetails.id && !enableHostedCreditCard) ||
      type === EPaymentMethods.MOBILE_WALLET
    ) {
      if (paymentDetails.isValid) {
        setStepPaymentStatus(true);
      } else {
        setStepPaymentStatus(false);
      }
    }
  }, [paymentDetails, type, setStepPaymentStatus]);

  const [isEnableModeApi, setEnableModeApi] = useState(true);

  useEffect(() => {
    // todo we have to change this logic
    // don't call this api in case of saved card otherwise it will call two times one with bin and other with wihout bin
    if (isEnabled && cartPaymentMode && isEnableModeApi && !isSavedCardEnabled) {
      setEnableModeApi(false);
      fetchCreditCardsAction(cartPaymentMode);
    }
  }, [isEnabled, fetchCreditCardsAction, cartPaymentMode, isEnableModeApi, isSavedCardEnabled]);

  const isShowPayment = useMemo(() => isEnabled || device.isMobile, [isEnabled, device.isMobile]);
  const { t } = useTranslation();
  const isFullRedemption = cartAmount === 0 && type === EPaymentMethods.CASHBACK && !loading;

  const isLbpToUsd = useLbpToUsd();

  return (
    <>
      {!isFullRedemption && (
        <>
          <S.Payment data-testid="__checkout_payments_container__">
            <Card.Title mb={3}> {t('payment_method')}</Card.Title>

            {isShowPayment && (
              <>
                {type ? (
                  <PaymentMethodList selectedPaymentMode={type} />
                ) : (
                  <S.SelectPaymentMethod>
                    {t('paymentForm.selectPaymentMethod')}
                  </S.SelectPaymentMethod>
                )}
              </>
            )}

            {loading && isShowPayment && <LinearProgress position="bottom" />}
          </S.Payment>

          {isLbpToUsd && type === EPaymentMethods.CREDIT_CARD && <ChangeCurrency />}

          <SecurePaymentsLogo />
        </>
      )}
    </>
  );
};

export default Payment;
